"use client";

import { useEffect, useState, useCallback, useRef, useMemo, memo } from "react";
import DynamicWidget from "@/components/DynamicWidget";
import useHistoryStore from "@/store/useHistoryStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faPaintBrush,
  faLock,
  faLockOpen,
  faArrowRight,
  faArrowLeft,
  faCopy,
  faSun,
  faMoon,
  faGripLines,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import {
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
} from "lucide-react";
import DeleteWidgetConfirm from "@/components/space/DeleteWidgetConfirm";
import WidgetSettings from "@/components/widgets/settings/WidgetSettings";
import { debounce } from "lodash";
import useStore from "@/store/useStore";
import { useResponsiveLayout, BREAKPOINTS } from "@/hooks/useResponsiveLayout";
import { compactLayoutVertically } from "@/hooks/useDragAndDrop";
// Removed old tooltip imports - now using custom PortalTooltip
import { completeWidgetControlsStep } from "@/utils/tourEvents";
import BetaBadge from "@/components/widgets/BetaBadge";
import { useWidgetPreview } from "@/hooks/useWidgetPreview";
import { spaceEvents } from "@/utils/analytics";

import { createPortal } from "react-dom";

// Custom tooltip component that renders in a portal to ensure it appears above everything
const PortalTooltip = ({ children, content, side = "top", ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !isOpen) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipElement = tooltipRef.current;

    if (!tooltipElement) return;

    const tooltipRect = tooltipElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    let y = triggerRect.top - tooltipRect.height - 8; // 8px gap

    // Adjust for side positioning
    if (side === "bottom") {
      y = triggerRect.bottom + 8;
    } else if (side === "left") {
      x = triggerRect.left - tooltipRect.width - 8;
      y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    } else if (side === "right") {
      x = triggerRect.right + 8;
      y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    }

    // Keep tooltip within viewport bounds
    if (x < 8) x = 8;
    if (x + tooltipRect.width > viewportWidth - 8) {
      x = viewportWidth - tooltipRect.width - 8;
    }
    if (y < 8) y = 8;
    if (y + tooltipRect.height > viewportHeight - 8) {
      y = viewportHeight - tooltipRect.height - 8;
    }

    setPosition({ x, y });
  }, [isOpen, side]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const tooltipContent =
    isOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={tooltipRef}
            className="fixed bg-black/90 text-white text-xs px-3 py-2 rounded-xl whitespace-nowrap pointer-events-none"
            style={{
              left: position.x,
              top: position.y,
              zIndex: 99999, // Extremely high z-index to ensure it appears above everything
              maxWidth: "200px",
              wordWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            {content}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {children}
      </div>
      {tooltipContent}
    </>
  );
};

// Memoize the DynamicWidget component
const MemoizedDynamicWidget = memo(DynamicWidget);

const EditableWidget = memo(function EditableWidget({
  id,
  layout,
  config,
  widget,
  isEditing,
  spaceId,
  spaceSlug,
  onSaveStatusChange,
  handleWidgetUpdate,
  onDelete,
  spaceData,
  previewMode,
  handleMouseDown,
  allWidgets,
  totalColumns,
  queueAction,
  onOpenSettings,
  onCloseSettings,
  // Mobile widget selection props
  selectedMobileWidgetId,
  isMobileDevice: isMobileDeviceProp,
  // Resize preview props
  onResizePreviewStart,
  onResizePreviewUpdate,
  onResizePreviewStop,
}) {
  const [isBeingDragged, setIsBeingDragged] = useState(false);

  const widgetRef = useRef(null);
  const [dimensions, setDimensions] = useState(null);
  const resizeObserver = useRef(null);
  const dimensionsRef = useRef(dimensions);

  // Add refs for the settings buttons
  const configButtonRef = useRef(null);
  const layoutButtonRef = useRef(null);

  const { windowWidth } = useResponsiveLayout();
  const isMobileDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    return windowWidth <= BREAKPOINTS.tablet;
  }, [windowWidth]);

  // Check if we should use compact controls (for mobile device or mobile preview)
  const useCompactControls = useMemo(() => {
    return isMobileDevice || previewMode === "mobile";
  }, [isMobileDevice, previewMode]);

  // Connect to global debug mode state
  const debugModeEnabled = useStore((state) => state.debugModeEnabled);
  const showDebug = debugModeEnabled;

  const pushAction = useHistoryStore((state) => state.pushAction);

  // Memoize widget styles
  const widgetStyles = useMemo(() => {
    const {
      padding,
      backgroundColor,
      borderColor,
      borderWidth,
      borderRadius,
      transparency,
      locked,
    } = layout.settings || {};
    const { width, height } = dimensionsRef.current || {};

    // Calculate background color with transparency
    let bgColor = backgroundColor || "transparent";
    if (
      backgroundColor &&
      transparency !== undefined &&
      widget.widget_type.type !== "single_media"
    ) {
      // Convert hex to rgba
      const r = parseInt(backgroundColor.slice(1, 3), 16);
      const g = parseInt(backgroundColor.slice(3, 5), 16);
      const b = parseInt(backgroundColor.slice(5, 7), 16);
      bgColor = `rgba(${r}, ${g}, ${b}, ${transparency / 100})`;
    }

    // Special handling for SingleMediaWidget
    if (widget.widget_type.type === "single_media") {
      return {
        width: "100%",
        height: "100%",
        padding: 0, // Remove padding
        backgroundColor: "transparent",
        border: "none", // Remove border for image widget
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
      };
    }

    // Special handling for MapWidget - edge-to-edge like single_media
    if (widget.widget_type.type === "map") {
      return {
        width: "100%",
        height: "100%",
        padding: 0, // Remove padding for edge-to-edge display
        backgroundColor: "transparent",
        border: "none", // Remove border for map widget
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
      };
    }

    // Special handling for VideoPlayerWidget - always no padding
    if (widget.widget_type.type === "video_player") {
      return {
        width: "100%",
        height: "100%",
        padding: 0, // Always no padding for video player widgets
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked
            ? bgColor === "transparent" || !bgColor
              ? "inset 0 0 0 2px rgba(100, 100, 100, 0.25)" // Stronger outline for transparent background
              : "0 0 0 1px rgba(255,255,255,0.75)"
            : "none",
      };
    }

    if (widget.widget_type.type === "spotify") {
      return {
        width: "100%",
        height: "100%",
        padding: 0,
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
      };
    }

    if (widget.widget_type.type === "apple_music") {
      return {
        width: "100%",
        height: "100%",
        padding: 0,
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
      };
    }

    // Special handling for ImageGalleryWidget - respect padding when there's a background
    if (widget.widget_type.type === "image_gallery") {
      const hasBackground =
        backgroundColor &&
        backgroundColor !== "transparent" &&
        !layout.settings?.backgroundColorGlobal;

      return {
        width: "100%",
        height: "100%",
        padding: hasBackground ? padding || "1rem" : 0, // Apply padding only when there's a background
        backgroundColor: bgColor, // Keep applying the background color
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked
            ? bgColor === "transparent" || !bgColor
              ? "inset 0 0 0 2px rgba(100, 100, 100, 0.25)" // Stronger outline for transparent background
              : "0 0 0 1px rgba(255,255,255,0.75)"
            : "none",
      };
    }

    // Special handling for rich text widgets with viewport-based padding adjustment
    if (widget.widget_type.type === "rich_text") {
      let adjustedPadding = padding || "1rem";

      // Check viewport width - if less than 1280px, remove vertical padding
      if (typeof window !== "undefined" && window.innerWidth < 1280) {
        if (typeof adjustedPadding === "string") {
          const paddingParts = adjustedPadding.trim().split(/\s+/);

          if (paddingParts.length === 1) {
            // Single value: set vertical to 0, keep horizontal
            adjustedPadding = `0 ${paddingParts[0]}`;
          } else if (paddingParts.length === 2) {
            // Two values: first is vertical, second is horizontal - set vertical to 0
            adjustedPadding = `0 ${paddingParts[1]}`;
          } else if (paddingParts.length === 4) {
            // Four values: top right bottom left - set top and bottom to 0
            adjustedPadding = `0 ${paddingParts[1]} 0 ${paddingParts[3]}`;
          }
        }
      } else if (
        // Original skinny rich text logic for mobile devices/preview
        layout[previewMode]?.columnHeight === 1 &&
        (isMobileDevice || previewMode === "mobile")
      ) {
        // Reduce only vertical padding (top/bottom) to about 1/3 of normal, keep horizontal padding normal
        if (typeof adjustedPadding === "string") {
          const paddingParts = adjustedPadding.trim().split(/\s+/);

          if (paddingParts.length === 1) {
            // Single value: apply reduced to top/bottom, normal to left/right
            const value = parseFloat(paddingParts[0]);
            const unit = paddingParts[0].replace(value.toString(), "");
            const reducedValue = value * 0.33;
            adjustedPadding = `${reducedValue}${unit} ${paddingParts[0]}`;
          } else if (paddingParts.length === 2) {
            // Two values: first is vertical, second is horizontal - reduce only vertical
            const verticalValue = parseFloat(paddingParts[0]);
            const verticalUnit = paddingParts[0].replace(
              verticalValue.toString(),
              ""
            );
            const reducedVertical = verticalValue * 0.33;
            adjustedPadding = `${reducedVertical}${verticalUnit} ${paddingParts[1]}`;
          } else if (paddingParts.length === 4) {
            // Four values: top right bottom left - reduce top and bottom
            const topValue = parseFloat(paddingParts[0]);
            const topUnit = paddingParts[0].replace(topValue.toString(), "");
            const bottomValue = parseFloat(paddingParts[2]);
            const bottomUnit = paddingParts[2].replace(
              bottomValue.toString(),
              ""
            );
            const reducedTop = topValue * 0.33;
            const reducedBottom = bottomValue * 0.33;
            adjustedPadding = `${reducedTop}${topUnit} ${paddingParts[1]} ${reducedBottom}${bottomUnit} ${paddingParts[3]}`;
          }
        }
      }

      return {
        width: "100%",
        height: "100%",
        padding: adjustedPadding, // Adjusted vertical padding for rich text widgets
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        transition:
          "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
        position: "relative",
        boxShadow:
          isEditing && !locked ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
      };
    }

    // Normal styling for other widgets
    return {
      width: "100%",
      height: "100%",
      padding,
      backgroundColor: bgColor,
      border: borderColor
        ? `${borderWidth || "1px"} solid ${borderColor}`
        : "none",
      borderRadius: borderRadius || "1.5rem",
      transition:
        "height 0.3s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out",
      position: "relative",
      boxShadow:
        isEditing && !locked ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
    };
  }, [
    layout.settings,
    dimensionsRef.current,
    widget.widget_type.type,
    isEditing,
    layout[previewMode],
    isMobileDevice,
    previewMode,
  ]);

  // Keep dimensionsRef in sync
  useEffect(() => {
    dimensionsRef.current = dimensions;
  }, [dimensions]);

  // Memoize the calculate dimensions function
  const calculateDimensions = useCallback(() => {
    if (!widgetRef.current) return;

    const containerWidth = widgetRef.current.offsetWidth;
    if (!containerWidth) return;

    const columns = layout?.[previewMode]?.columns || 4;
    const columnHeight = layout?.[previewMode]?.columnHeight || columns;
    const gapPixels = 24;

    const totalGapWidth = gapPixels * (columns - 1);
    const usableWidth = containerWidth - totalGapWidth;
    const columnWidth = usableWidth / columns;
    const width = containerWidth;
    const baseColumnHeight = columnWidth;
    const totalGapHeight = gapPixels * (columnHeight - 1);
    const height = baseColumnHeight * columnHeight + totalGapHeight;

    setDimensions({ width, height });
  }, [layout, previewMode]);

  // Handle custom mouse down for drag and drop
  const handleWidgetMouseDown = useCallback(
    (e) => {
      if (
        e.target.closest(".widget-controls") ||
        e.target.closest(".widget-plain-text") ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      // On mobile devices, only allow dragging from the drag handle
      if (isMobileDevice && !e.target.closest(".mobile-drag-handle")) {
        return;
      }

      if (isEditing && handleMouseDown && !layout.settings?.locked) {
        e.preventDefault();
        setIsBeingDragged(true);
        handleMouseDown(e, id);

        // Add listeners to detect when dragging stops
        const handleMouseUp = () => {
          setIsBeingDragged(false);
          document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mouseup", handleMouseUp);
      }
    },
    [isEditing, handleMouseDown, id, layout.settings?.locked, isMobileDevice]
  );

  // Setup ResizeObserver
  useEffect(() => {
    if (!widgetRef.current) return;

    calculateDimensions();

    resizeObserver.current = new ResizeObserver(
      debounce((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width > 0) {
            calculateDimensions();
          }
        }
      }, 100)
    );

    resizeObserver.current.observe(widgetRef.current);

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [calculateDimensions]);

  // Handle window resize with debounce
  useEffect(() => {
    const handleResize = debounce(calculateDimensions, 100);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateDimensions]);

  // Recalculate on layout or preview mode change
  useEffect(() => {
    calculateDimensions();
  }, [layout, previewMode, calculateDimensions]);

  const handleConfigUpdate = useCallback(
    (newConfig, batchUpdate = null) => {
      console.log("EditableWidget handleConfigUpdate:", newConfig);

      // If this is a batch update with multiple widgets
      if (batchUpdate && batchUpdate.widgets) {
        // Filter widgets to only include those of the same type as the current widget
        const filteredWidgets = {};

        Object.entries(batchUpdate.widgets).forEach(([widgetId, updates]) => {
          // Find the widget in allWidgets
          const targetWidget = allWidgets.find((w) => w.id === widgetId);
          // Only include widgets of the same type as the current widget
          if (
            targetWidget &&
            targetWidget.widget_type.type === widget.widget_type.type
          ) {
            filteredWidgets[widgetId] = updates;
            // Apply to local state
            handleWidgetUpdate(widgetId, updates);
          }
        });

        // Queue the filtered batch update for autosave
        queueAction(
          {
            widgets: filteredWidgets,
          },
          "CONFIG_CHANGE_BATCH"
        );

        // Log batch update
        console.log(
          `Batch config updated for ${
            Object.keys(filteredWidgets).length
          } widgets of type ${widget.widget_type.type}`
        );
        return;
      }

      // Apply to local state
      handleWidgetUpdate(id, { config: newConfig });

      // Queue for autosave and add to history
      queueAction(
        {
          widgets: {
            [id]: {
              config: newConfig,
              last_autosave_at: new Date().toISOString(),
            },
          },
        },
        "CONFIG_CHANGE"
      );

      // Log for debugging
      console.log(`Config updated for widget ${id}`, newConfig);
    },
    [id, handleWidgetUpdate, queueAction, widget.widget_type.type, allWidgets]
  );

  const handleLayoutChange = useCallback(
    (newLayout, batchUpdate = null) => {
      // If this is a batch update with multiple widgets
      if (batchUpdate && batchUpdate.widgets) {
        // For layout changes (position updates), we should NOT filter by widget type
        // because position changes can affect any widget type
        const widgetsToUpdate = {};

        Object.entries(batchUpdate.widgets).forEach(([widgetId, updates]) => {
          // Find the widget in allWidgets to ensure it exists
          const targetWidget = allWidgets.find((w) => w.id === widgetId);
          if (targetWidget) {
            widgetsToUpdate[widgetId] = updates;
            // Apply to local state
            handleWidgetUpdate(widgetId, updates);
          }
        });

        // Queue the batch update for autosave
        queueAction(
          {
            widgets: widgetsToUpdate,
          },
          "LAYOUT_CHANGE_BATCH"
        );

        // Log batch update
        console.log(
          `Batch layout updated for ${Object.keys(widgetsToUpdate).length} widgets`
        );
        return;
      }

      // Regular single widget update
      const updatedLayout = {
        ...layout,
        ...newLayout,
        // Preserve settings if they exist in the update
        settings: {
          ...(layout.settings || {}),
          ...(newLayout.settings || {}),
        },
      };

      // Apply locally first
      handleWidgetUpdate(id, { layout: updatedLayout });

      // Queue for autosave and add to history
      queueAction(
        {
          widgets: {
            [id]: {
              layout: updatedLayout,
              last_autosave_at: new Date().toISOString(),
            },
          },
        },
        "LAYOUT_CHANGE"
      );

      // Recalculate dimensions after update
      calculateDimensions();

      // Log for debugging
      console.log(`Layout updated for widget ${id}`, updatedLayout);
    },
    [
      id,
      layout,
      handleWidgetUpdate,
      queueAction,
      calculateDimensions,
      allWidgets,
    ]
  );

  const handleSettingsOpen = useCallback(
    (type) => {
      if (onOpenSettings) {
        onOpenSettings(id, type);
      }

      // Trigger tour completion for widget controls
      if (type === "layout") {
        completeWidgetControlsStep();
      }
    },
    [id, onOpenSettings]
  );

  const handleSettingsClose = useCallback(() => {
    if (onCloseSettings) {
      onCloseSettings();
    }
  }, [onCloseSettings]);

  const handleLockToggle = useCallback(() => {
    const newLayout = {
      ...layout,
      settings: {
        ...layout.settings,
        locked: !layout.settings?.locked,
      },
    };
    handleWidgetUpdate(id, { layout: newLayout });
    queueAction(
      {
        widgets: {
          [id]: {
            layout: newLayout,
            last_autosave_at: new Date().toISOString(),
          },
        },
      },
      "LAYOUT_CHANGE"
    );
  }, [id, layout, handleWidgetUpdate, queueAction]);

  const handleVerticalAlignment = useCallback(
    (alignment) => {
      const newLayout = {
        ...layout,
        settings: {
          ...layout.settings,
          verticalAlignment: alignment,
        },
      };
      handleWidgetUpdate(id, { layout: newLayout });
      queueAction(
        {
          widgets: {
            [id]: {
              layout: newLayout,
              last_autosave_at: new Date().toISOString(),
            },
          },
        },
        "LAYOUT_CHANGE"
      );
    },
    [id, layout, handleWidgetUpdate, queueAction]
  );

  const handleColumnResize = useCallback(
    (newColumns, newColumnHeight) => {
      console.log(`[Resize Debug] Starting resize for widget ${id}:`, {
        newColumns,
        newColumnHeight,
        currentLayout: layout[previewMode],
        previewMode,
        allWidgetsCount: allWidgets.length,
      });

      // Validate against mini_required
      if (
        widget.widget_type.mini_required &&
        !(
          (newColumns === 2 && newColumnHeight === 2) || // 2x2
          (newColumns === 2 && newColumnHeight === 4) || // 2x4
          (newColumns === 4 && newColumnHeight === 2)
        ) // 4x2
      ) {
        return;
      }

      // Validate against skinny_required
      if (widget.widget_type.skinny_required && newColumnHeight !== 1) {
        return;
      }

      // Capture the previous state for all widgets before making changes
      const previousState = {
        widgets: allWidgets.map((widget) => ({
          ...widget,
          layout: JSON.parse(JSON.stringify(widget.layout)),
          config: JSON.parse(JSON.stringify(widget.config)),
        })),
      };

      // Get current layout values
      const currentStartCol = layout[previewMode]?.startColumn || 0;
      const currentPosition = layout[previewMode]?.position || 0;

      // Calculate new startColumn, adjusting if resizing would push widget off the right edge
      let newStartColumn = currentStartCol;
      const maxColumns = totalColumns || 12;

      // If widget would exceed right edge after resize, adjust startColumn
      if (currentStartCol + newColumns > maxColumns) {
        newStartColumn = Math.max(0, maxColumns - newColumns);
      }

      // Apply the resize to the current viewport only
      console.log(
        `[Resize Debug] User resized widget in ${previewMode} viewport`
      );

      const newDimensions = {
        columns: newColumns,
        columnHeight: newColumnHeight,
        startColumn: newStartColumn,
        position: currentPosition,
      };

      // Create updated layout for current viewport only
      const updatedLayout = {
        ...layout,
        [previewMode]: {
          ...layout[previewMode],
          ...newDimensions,
        },
      };

      console.log(
        `[Resize Debug] Updated widget layout for ${previewMode}:`,
        updatedLayout[previewMode]
      );

      // Handle layout changes for current viewport only
      const calculateLayoutChanges = () => {
        // Create a working copy of all widgets
        const workingWidgets = allWidgets.map((w) => ({
          ...w,
          layout: JSON.parse(JSON.stringify(w.layout)), // Deep clone layout
        }));

        console.log(
          "[Resize Debug] Working widgets created:",
          workingWidgets.length
        );

        // Update the resizing widget in the working copy
        const resizingWidget = workingWidgets.find((w) => w.id === id);
        if (!resizingWidget) {
          console.error("[Resize Debug] Could not find resizing widget!");
          return {};
        }

        // Apply the updated layout to the resizing widget
        resizingWidget.layout = updatedLayout;

        // Build widget map for collision detection
        const widgetMap = new Map();
        const lockedWidgets = [];

        workingWidgets.forEach((item) => {
          const itemLayout = item.layout[previewMode];
          const widgetInfo = {
            id: item.id,
            position: itemLayout?.position || 0,
            height: itemLayout?.columnHeight || 4,
            startColumn: itemLayout?.startColumn || 0,
            columns: itemLayout?.columns || 4,
            locked: item.layout?.settings?.locked || false,
            item: item,
          };

          widgetMap.set(item.id, widgetInfo);

          if (widgetInfo.locked) {
            lockedWidgets.push(widgetInfo);
          }
        });

        console.log("[Resize Debug] Widget map built:", {
          totalWidgets: widgetMap.size,
          lockedWidgets: lockedWidgets.length,
        });

        // Get the resizing widget from map
        const resizingInfo = widgetMap.get(id);
        let adjustedPosition = resizingInfo.position;

        // Check for locked widget conflicts
        for (const lockedWidget of lockedWidgets) {
          const hasVerticalOverlap = !(
            adjustedPosition + newColumnHeight <= lockedWidget.position ||
            adjustedPosition >= lockedWidget.position + lockedWidget.height
          );

          const hasHorizontalOverlap = !(
            newStartColumn + newColumns <= lockedWidget.startColumn ||
            newStartColumn >= lockedWidget.startColumn + lockedWidget.columns
          );

          if (hasVerticalOverlap && hasHorizontalOverlap) {
            adjustedPosition = Math.max(
              adjustedPosition,
              lockedWidget.position + lockedWidget.height
            );
            console.log(
              "[Resize Debug] Adjusted position due to locked widget:",
              adjustedPosition
            );
          }
        }

        // Update resizing widget position if adjusted
        if (adjustedPosition !== resizingInfo.position) {
          resizingInfo.position = adjustedPosition;
          resizingWidget.layout[previewMode].position = adjustedPosition;
        }

        // Track all position changes
        const positionChanges = new Map();

        // Add the resizing widget's changes for current viewport only
        const currentLayout = updatedLayout[previewMode];
        positionChanges.set(id, {
          position: currentLayout.position,
          startColumn: currentLayout.startColumn,
          columns: currentLayout.columns,
          columnHeight: currentLayout.columnHeight,
        });

        // Process layout conflicts
        const processQueue = [];

        // Find initial conflicts
        widgetMap.forEach((widget) => {
          if (widget.locked || widget.id === id) return;

          const hasVerticalOverlap = !(
            adjustedPosition + newColumnHeight <= widget.position ||
            adjustedPosition >= widget.position + widget.height
          );

          const hasHorizontalOverlap = !(
            newStartColumn + newColumns <= widget.startColumn ||
            newStartColumn >= widget.startColumn + widget.columns
          );

          if (hasVerticalOverlap && hasHorizontalOverlap) {
            const pushAmount =
              adjustedPosition + newColumnHeight - widget.position;
            if (pushAmount > 0) {
              console.log(
                `[Resize Debug] Widget ${widget.id} needs to be pushed by ${pushAmount}`
              );
              processQueue.push({
                widget: widget,
                newPosition: widget.position + pushAmount,
              });
            }
          }
        });

        console.log(
          "[Resize Debug] Initial conflicts found:",
          processQueue.length
        );

        // Process the queue
        const movedWidgets = new Map();

        while (processQueue.length > 0) {
          const { widget, newPosition } = processQueue.shift();

          if (
            movedWidgets.has(widget.id) &&
            movedWidgets.get(widget.id) >= newPosition
          ) {
            continue;
          }

          let finalPosition = newPosition;

          // Check locked widget conflicts
          for (const lockedWidget of lockedWidgets) {
            const wouldOverlapVertically = !(
              finalPosition + widget.height <= lockedWidget.position ||
              finalPosition >= lockedWidget.position + lockedWidget.height
            );

            const wouldOverlapHorizontally = !(
              widget.startColumn + widget.columns <= lockedWidget.startColumn ||
              widget.startColumn >=
                lockedWidget.startColumn + lockedWidget.columns
            );

            if (wouldOverlapVertically && wouldOverlapHorizontally) {
              finalPosition = Math.max(
                finalPosition,
                lockedWidget.position + lockedWidget.height
              );
            }
          }

          // CRITICAL FIX: Check conflicts with previously moved widgets
          movedWidgets.forEach((movedPosition, movedWidgetId) => {
            const movedWidgetInfo = widgetMap.get(movedWidgetId);
            if (!movedWidgetInfo) return;

            const wouldOverlapVertically = !(
              finalPosition + widget.height <= movedPosition ||
              finalPosition >= movedPosition + movedWidgetInfo.height
            );

            const wouldOverlapHorizontally = !(
              widget.startColumn + widget.columns <=
                movedWidgetInfo.startColumn ||
              widget.startColumn >=
                movedWidgetInfo.startColumn + movedWidgetInfo.columns
            );

            if (wouldOverlapVertically && wouldOverlapHorizontally) {
              finalPosition = Math.max(
                finalPosition,
                movedPosition + movedWidgetInfo.height
              );
            }
          });

          movedWidgets.set(widget.id, finalPosition);

          // CRITICAL FIX: Update the widget position in the map immediately
          widget.position = finalPosition;
          widgetMap.set(widget.id, widget);

          // Track this change
          positionChanges.set(widget.id, {
            position: finalPosition,
            startColumn: widget.startColumn,
            columns: widget.columns,
            columnHeight: widget.height,
          });

          console.log(
            `[Resize Debug] Widget ${widget.id} moved to position ${finalPosition}`
          );

          // Check for new layout conflicts using updated positions
          widgetMap.forEach((otherWidget) => {
            if (
              otherWidget.id === widget.id ||
              otherWidget.locked ||
              movedWidgets.has(otherWidget.id) ||
              otherWidget.id === id
            ) {
              return;
            }

            // Use the current position (may have been updated by previous moves)
            const currentOtherPosition = movedWidgets.has(otherWidget.id)
              ? movedWidgets.get(otherWidget.id)
              : otherWidget.position;

            const hasVerticalOverlap = !(
              finalPosition + widget.height <= currentOtherPosition ||
              finalPosition >= currentOtherPosition + otherWidget.height
            );

            const hasHorizontalOverlap = !(
              widget.startColumn + widget.columns <= otherWidget.startColumn ||
              widget.startColumn >=
                otherWidget.startColumn + otherWidget.columns
            );

            if (hasVerticalOverlap && hasHorizontalOverlap) {
              const otherPushAmount =
                finalPosition + widget.height - currentOtherPosition;
              if (otherPushAmount > 0) {
                const newOtherPosition = currentOtherPosition + otherPushAmount;

                // Only add to queue if not already processed with equal or greater position
                const existingQueueItem = processQueue.find(
                  (q) => q.widget.id === otherWidget.id
                );
                if (existingQueueItem) {
                  // Update existing queue item if new position is greater
                  if (newOtherPosition > existingQueueItem.newPosition) {
                    existingQueueItem.newPosition = newOtherPosition;
                  }
                } else if (
                  !movedWidgets.has(otherWidget.id) ||
                  movedWidgets.get(otherWidget.id) < newOtherPosition
                ) {
                  processQueue.push({
                    widget: otherWidget,
                    newPosition: newOtherPosition,
                  });
                }
              }
            }
          });
        }

        console.log(
          "[Resize Debug] Total position changes:",
          positionChanges.size
        );

        // ENHANCED DEBUGGING: Validate no overlaps in final positions
        const validateNoOverlaps = () => {
          const positions = Array.from(positionChanges.entries()).map(
            ([widgetId, changes]) => ({
              id: widgetId,
              position: changes.position,
              height: changes.columnHeight,
              startColumn: changes.startColumn,
              columns: changes.columns,
            })
          );

          for (let i = 0; i < positions.length; i++) {
            for (let j = i + 1; j < positions.length; j++) {
              const widget1 = positions[i];
              const widget2 = positions[j];

              const hasVerticalOverlap = !(
                widget1.position + widget1.height <= widget2.position ||
                widget1.position >= widget2.position + widget2.height
              );

              const hasHorizontalOverlap = !(
                widget1.startColumn + widget1.columns <= widget2.startColumn ||
                widget1.startColumn >= widget2.startColumn + widget2.columns
              );

              if (hasVerticalOverlap && hasHorizontalOverlap) {
                console.error(
                  `[Resize Debug] OVERLAP DETECTED between widgets ${widget1.id} and ${widget2.id}!`,
                  { widget1, widget2 }
                );
                return false;
              }
            }
          }
          console.log(
            "[Resize Debug] ✅ No overlaps detected in final positions"
          );
          return true;
        };

        validateNoOverlaps();
        return positionChanges;
      };

      // Calculate all position changes
      const positionChanges = calculateLayoutChanges();

      // Create batch update object
      const widgetUpdates = {};

      positionChanges.forEach((changes, widgetId) => {
        const targetWidget = allWidgets.find((w) => w.id === widgetId);
        if (targetWidget) {
          widgetUpdates[widgetId] = {
            layout: {
              ...targetWidget.layout,
              [previewMode]: {
                ...targetWidget.layout[previewMode],
                ...changes,
              },
            },
          };
          console.log(
            `[Resize Debug] Update prepared for widget ${widgetId}:`,
            changes
          );
        }
      });

      // Apply all updates at once
      if (Object.keys(widgetUpdates).length > 0) {
        console.log(
          "[Resize Debug] Applying layout updates to",
          Object.keys(widgetUpdates).length,
          "widgets"
        );

        handleLayoutChange(widgetUpdates[id].layout, {
          widgets: widgetUpdates,
        });

        // Add the resize action to history store with previous state
        const newState = {
          widgets: allWidgets.map((widget) => {
            if (widgetUpdates[widget.id]) {
              return {
                ...widget,
                layout: {
                  ...widget.layout,
                  ...widgetUpdates[widget.id].layout,
                },
              };
            }
            return widget;
          }),
        };

        // Determine the resize type for better description
        const fromSize = `${layout[previewMode]?.columns || 4}×${layout[previewMode]?.columnHeight || 4}`;
        const toSize = `${newColumns}×${newColumnHeight}`;
        const widgetTypeName =
          widget.widget_type?.name || widget.widget_type?.type || "widget";

        pushAction({
          type: "WIDGET_RESIZE",
          payload: {
            widgetId: id,
            widgetType: widgetTypeName,
            fromSize,
            toSize,
            previewMode,
            changes: widgetUpdates,
          },
          previousState,
          newState,
          description: `Resized ${widgetTypeName} from ${fromSize} to ${toSize}`,
          timestamp: new Date().toISOString(),
        });

        // Force layout recalculation after DOM updates
        requestAnimationFrame(() => {
          console.log("[Resize Debug] Triggering layout recalculation");
          const masonryContainer = document.querySelector(
            "[data-masonry-container]"
          );
          if (masonryContainer && masonryContainer.__ref) {
            masonryContainer.__ref.recalculateLayout();
          }
        });

        // Apply compaction after a delay
        setTimeout(() => {
          console.log("[Resize Debug] Starting compaction phase");
          const currentWidgets = allWidgets.map((widget) => {
            if (widgetUpdates[widget.id]) {
              return {
                ...widget,
                layout: {
                  ...widget.layout,
                  ...widgetUpdates[widget.id].layout,
                },
              };
            }
            return widget;
          });

          const compactedLayout = compactLayoutVertically(
            currentWidgets,
            previewMode,
            totalColumns || 12,
            [id]
          );

          // Find changes from compaction
          const compactedUpdates = {};
          let hasCompactionChanges = false;

          compactedLayout.forEach((widget) => {
            if (widget.id === id) return;

            const currentWidget = currentWidgets.find(
              (w) => w.id === widget.id
            );
            if (!currentWidget) return;

            if (
              widget.layout[previewMode]?.position !==
              currentWidget.layout[previewMode]?.position
            ) {
              hasCompactionChanges = true;
              compactedUpdates[widget.id] = {
                layout: { ...widget.layout },
              };
            }
          });

          if (hasCompactionChanges) {
            console.log("[Resize Debug] Applying compaction changes");
            handleLayoutChange(null, {
              widgets: compactedUpdates,
            });

            // Update the new state with compaction changes for the history
            const finalState = {
              widgets: compactedLayout.map((widget) => ({
                ...widget,
                layout: JSON.parse(JSON.stringify(widget.layout)),
                config: JSON.parse(JSON.stringify(widget.config)),
              })),
            };

            // Update the history action with the final state after compaction
            const historyState = useHistoryStore.getState();
            const lastAction = historyState.getCurrentAction();
            if (lastAction && lastAction.type === "WIDGET_RESIZE") {
              // Update the last action's new state to include compaction
              lastAction.newState = finalState;
            }

            requestAnimationFrame(() => {
              const masonryContainer = document.querySelector(
                "[data-masonry-container]"
              );
              if (masonryContainer && masonryContainer.__ref) {
                masonryContainer.__ref.recalculateLayout();
              }
            });
          }
        }, 200);
      }
    },
    [
      widget.widget_type,
      layout,
      previewMode,
      handleLayoutChange,
      id,
      allWidgets,
      totalColumns,
    ]
  );

  const handleDuplicate = useCallback(() => {
    // Get the next available position (highest position + 1)
    const positions = allWidgets.map((w) => {
      const desktopPos = w.layout?.desktop?.position;
      const tabletPos = w.layout?.tablet?.position;
      const mobilePos = w.layout?.mobile?.position;
      return Math.max(
        typeof desktopPos === "number" ? desktopPos : -1,
        typeof tabletPos === "number" ? tabletPos : -1,
        typeof mobilePos === "number" ? mobilePos : -1,
        -1
      );
    });
    const nextPosition = Math.max(...positions, -1) + 1;

    // Create the widget data with all viewport layouts
    const widgetData = {
      widget: {
        id: widget.id,
        config: { ...config },
        layout: {
          desktop: {
            ...layout.desktop,
            position: nextPosition,
          },
          tablet: {
            ...layout.tablet,
            position: nextPosition,
          },
          mobile: {
            ...layout.mobile,
            position: nextPosition,
          },
          settings: { ...layout.settings },
        },
      },
      spaceId,
      draftId: spaceId,
    };

    queueAction(widgetData, "ADD_WIDGET", null);
  }, [widget, config, layout, spaceId, allWidgets, queueAction]);

  const handleThemeToggle = useCallback(() => {
    if (widget.widget_type.type === "mini_link") {
      const currentSettings = widget.layout.settings || {};
      const isDarkMode = currentSettings.isDarkMode ?? false;
      const newIsDarkMode = !isDarkMode;

      handleWidgetUpdate(widget.id, {
        layout: {
          ...widget.layout,
          settings: {
            ...currentSettings,
            isDarkMode: newIsDarkMode,
            backgroundColor: newIsDarkMode ? "#1a1a1a" : "#ffffff",
            fontColor: newIsDarkMode ? "#ffffff" : "#000000",
          },
        },
      });
    } else if (widget.widget_type.type === "map") {
      const currentIsDarkMode = config?.isDarkMode ?? false;
      const newIsDarkMode = !currentIsDarkMode;

      handleWidgetUpdate(widget.id, {
        config: {
          ...config,
          isDarkMode: newIsDarkMode,
        },
      });
    }
  }, [widget, config, handleWidgetUpdate]);

  // Use the preview hook to get effective settings with real-time preview
  const effectiveSettings = useWidgetPreview(id, layout?.settings || {});

  // Memoize the widget props
  const widgetProps = useMemo(
    () => ({
      widget,
      layout: {
        ...layout,
        settings: effectiveSettings,
      },
      config,
      settings: effectiveSettings,
      onConfigUpdate: handleConfigUpdate,
      handleWidgetUpdate,
      isEditing,
      onDelete: () => onDelete(id),
      debugModeEnabled: showDebug,
      spaceData,
      spaceSlug,
      widgetId: id,
      previewMode,
      activeGalleryId:
        widget.widget_type.type === "image_gallery"
          ? allWidgets?.activeGalleryId
          : undefined,
    }),
    [
      widget,
      layout,
      config,
      effectiveSettings,
      handleConfigUpdate,
      isEditing,
      onDelete,
      id,
      showDebug,
      spaceData,
      spaceSlug,
      previewMode,
      allWidgets?.activeGalleryId,
    ]
  );

  return (
    <div
      ref={widgetRef}
      data-widget-id={id}
      data-edge-to-edge={
        widget.widget_type.type === "single_media" ||
        widget.widget_type.type === "image_gallery" ||
        widget.widget_type.type === "map" ||
        (widget.widget_type.type === "video_player" &&
          (!layout.settings?.backgroundColor ||
            layout.settings?.backgroundColor === "transparent" ||
            layout.settings?.backgroundColorGlobal))
      }
      className={`relative min-w-0 h-full ${
        isEditing && !layout.settings?.locked && !isMobileDevice
          ? "group hover:ring-2 hover:ring-blue-500/30"
          : ""
      } ${
        isBeingDragged ? "ring-2 ring-blue-500/70" : ""
      } transition-all duration-300 ease-in-out transform ${
        widget.widget_type.type === "image_gallery" &&
        (!layout.settings?.backgroundColor ||
          layout.settings?.backgroundColor === "transparent" ||
          layout.settings?.backgroundColorGlobal)
          ? "p-0"
          : ""
      } ${
        widget.widget_type.type === "image_gallery" &&
        isEditing &&
        (!layout.settings?.backgroundColor ||
          layout.settings?.backgroundColor === "transparent" ||
          layout.settings?.backgroundColorGlobal)
          ? "edit-gallery-transparent-bg"
          : ""
      } ${widget.isNewlyAdded ? "newly-added-widget" : ""}`}
      style={widgetStyles}
      onMouseDown={handleWidgetMouseDown}
    >
      <style jsx>{`
        .edit-gallery-transparent-bg {
          background-color: rgba(100, 100, 100, 0.05);
        }

        /* Highlight animation for newly added widgets */
        :global(.newly-added-widget) {
          animation: highlight-pulse 5s ease-in-out;
          position: relative;
          z-index: 5;
        }

        @keyframes highlight-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(212, 255, 0, 0.1);
          }
          20% {
            box-shadow: 0 0 0 15px rgba(212, 255, 0, 0.6);
          }
          40% {
            box-shadow: 0 0 0 10px rgba(212, 255, 0, 0.4);
          }
          60% {
            box-shadow: 0 0 0 15px rgba(212, 255, 0, 0.6);
          }
          80% {
            box-shadow: 0 0 0 5px rgba(212, 255, 0, 0.3);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(212, 255, 0, 0);
          }
        }
      `}</style>

      {/* Beta badge - shows in both edit and view mode */}
      {widget.widget_type?.is_beta &&
        spaceData?.space_data?.show_beta_tags !== false &&
        selectedMobileWidgetId !== id && <BetaBadge />}

      {showDebug && isEditing && (
        <div className="absolute top-0 left-0 right-0 bg-black/80 text-white text-xs p-2 z-[1000] font-mono">
          <div className="text-yellow-400 font-bold mb-1">Widget ID: {id}</div>
          <div>Position: {layout[previewMode]?.position}</div>
          <div>StartCol: {layout[previewMode]?.startColumn}</div>
          <div>Cols: {layout[previewMode]?.columns}</div>
          <div>Height: {layout[previewMode]?.columnHeight}</div>
        </div>
      )}

      <div className="w-full h-full transition-all duration-300 ease-in-out">
        <MemoizedDynamicWidget {...widgetProps} />
      </div>

      {/* Only show edit controls if not on mobile */}
      {isEditing && !isMobileDevice && (
        <>
          {!layout.settings?.locked && (
            <>
              <div
                className="absolute -top-4 left-2 flex items-center gap-1.5 z-[9999] md:opacity-0 md:group-hover:opacity-100 opacity-100 transition-opacity duration-200"
                data-tour-target="widget-top-controls"
              >
                <div className="flex bg-dark-surface rounded-lg p-0.5 hover:shadow-xl transition-all duration-200 border border-neutral-light/30">
                  <PortalTooltip content="Delete widget" side="top">
                    <DeleteWidgetConfirm
                      onDelete={() => {
                        // Track widget deletion
                        const session = useStore.getState().session;
                        spaceEvents.widgetDelete(
                          spaceId,
                          widget.widget_type?.type || widget.type,
                          id,
                          {
                            widget_name:
                              widget.widget_type?.name || widget.name,
                            position: layout[previewMode]?.position,
                          },
                          session
                        );

                        onDelete?.(id);
                      }}
                      onCancel={() => {}}
                      className="p-1.5 w-7 h-7 rounded-md text-white/70 transition-colors outline-none flex items-center justify-center hover:bg-dark-elevated"
                      popoverClassName="left-0"
                    />
                  </PortalTooltip>

                  {(widget.widget_type.type === "mini_link" ||
                    widget.widget_type.type === "map") && (
                    <PortalTooltip
                      content={
                        widget.widget_type.type === "mini_link"
                          ? widget.layout.settings?.isDarkMode
                            ? "Switch to light mode"
                            : "Switch to dark mode"
                          : (config?.isDarkMode ?? false)
                            ? "Switch to light mode"
                            : "Switch to dark mode"
                      }
                      side="top"
                    >
                      <button
                        onClick={handleThemeToggle}
                        className="p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center hover:bg-dark-elevated"
                      >
                        <FontAwesomeIcon
                          icon={
                            widget.widget_type.type === "mini_link"
                              ? widget.layout.settings?.isDarkMode
                                ? faSun
                                : faMoon
                              : (config?.isDarkMode ?? false)
                                ? faSun
                                : faMoon
                          }
                          className="h-4 w-4 flex-shrink-0"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {widget.widget_type?.config_enabled !== false &&
                  (widget.widget_type?.type === "mini_link" ||
                    widget.widget_type?.type === "file" ||
                    widget.widget_type?.type === "rich_text" ||
                    widget.widget_type?.type === "spotify" ||
                    widget.widget_type?.type === "apple_music" ||
                    widget.widget_type?.type === "calendly" ||
                    widget.widget_type?.type === "opentable" ||
                    widget.widget_type?.type === "video_player" ||
                    widget.widget_type?.type === "links" ||
                    widget.widget_type?.type === "image_gallery" ||
                    widget.widget_type?.type === "single_media" ||
                    widget.widget_type?.type === "business_hours" ||
                    widget.widget_type?.type === "location" ||
                    widget.widget_type?.type === "bio" ||
                    widget.widget_type?.type === "photo_album" ||
                    (widget.widget_type?.default_config !== null &&
                      widget.widget_type?.default_config &&
                      Object.keys(widget.widget_type?.default_config || {})
                        .length > 0)) ? (
                    <PortalTooltip
                      content="Edit widget content and settings"
                      side="top"
                    >
                      <button
                        onClick={() => handleSettingsOpen("config")}
                        className="p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center hover:bg-dark-elevated"
                        ref={configButtonRef}
                      >
                        <FontAwesomeIcon
                          icon={
                            widget.widget_type?.type === "mini_link" ||
                            widget.widget_type?.type === "rich_text"
                              ? faLink
                              : faCog
                          }
                          size="xs"
                          className="flex-shrink-0"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      </button>
                    </PortalTooltip>
                  ) : null}

                  {widget.widget_type.settings_enabled !== false && (
                    <PortalTooltip content="Customize appearance" side="top">
                      <button
                        onClick={() => handleSettingsOpen("layout")}
                        className="p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center hover:bg-dark-elevated"
                        ref={layoutButtonRef}
                      >
                        <FontAwesomeIcon
                          icon={faPaintBrush}
                          size="xs"
                          className="flex-shrink-0"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      </button>
                    </PortalTooltip>
                  )}
                </div>
              </div>

              {/* Size Controls as button group at bottom */}
              <div
                className="widget-controls absolute -bottom-4 left-1/2 -translate-x-1/2 md:opacity-0 md:group-hover:opacity-100 opacity-100 transition-opacity duration-200 z-[9999] pointer-events-auto"
                data-tour-target="widget-resize"
                onMouseLeave={() => onResizePreviewStop?.()}
              >
                <div className="inline-flex h-10 rounded-lg bg-black shadow-lg border border-neutral-light/30">
                  {/* Mini (2x2) */}
                  {widget.widget_type.mini && (
                    <PortalTooltip content="Mini (2×2)" side="top">
                      <button
                        onClick={() => handleColumnResize(2, 2)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 2, 2)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columns === 2 &&
                          layout[previewMode].columnHeight === 2
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-3.5 h-3.5" : "w-4 h-4"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {/* Wide (4x2) */}
                  {widget.widget_type.wide && (
                    <PortalTooltip content="Wide (4×2)" side="top">
                      <button
                        onClick={() => handleColumnResize(4, 2)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 4, 2)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columns === 4 &&
                          layout[previewMode].columnHeight === 2
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-5 h-3.5" : "w-6 h-4"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {/* Tall (2x4) */}
                  {widget.widget_type.tall && (
                    <PortalTooltip content="Tall (2×4)" side="top">
                      <button
                        onClick={() => handleColumnResize(2, 4)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 2, 4)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columns === 2 &&
                          layout[previewMode].columnHeight === 4
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-3.5 h-5" : "w-4 h-6"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {/* Standard (4x4) */}
                  {widget.widget_type.standard && (
                    <PortalTooltip content="Standard (4×4)" side="top">
                      <button
                        onClick={() => handleColumnResize(4, 4)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 4, 4)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columns === 4 &&
                          layout[previewMode].columnHeight === 4
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-5 h-5" : "w-6 h-6"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {/* Super Tall (4x6) */}
                  {widget.widget_type.super_tall && (
                    <PortalTooltip content="Super Tall (4×6)" side="top">
                      <button
                        onClick={() => handleColumnResize(4, 6)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 4, 6)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columns === 4 &&
                          layout[previewMode].columnHeight === 6
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-5 h-7" : "w-6 h-8"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {/* Super Wide (8x4) */}
                  {widget.widget_type.super_wide && !useCompactControls && (
                    <PortalTooltip content="Super Wide (8×4)" side="top">
                      <button
                        onClick={() => handleColumnResize(8, 4)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 8, 4)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columns === 8 &&
                          layout[previewMode].columnHeight === 4
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-7 h-5" : "w-8 h-6"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}

                  {/* Skinny (4x1 or 8x1) */}
                  {widget.widget_type.skinny && (
                    <PortalTooltip content="Skinny (4×1)" side="top">
                      <button
                        onClick={() => handleColumnResize(4, 1)}
                        onMouseEnter={() => onResizePreviewStart?.(id, 4, 1)}
                        onMouseLeave={() => onResizePreviewStop?.()}
                        className={`${
                          useCompactControls ? "px-1.5" : "px-2"
                        } h-full flex items-center justify-center first:rounded-l-lg last:rounded-r-lg ${
                          layout[previewMode].columnHeight === 1
                            ? "bg-primary text-white/70"
                            : "text-white/70 hover:bg-primary hover:bg-opacity-20"
                        }`}
                      >
                        <div
                          className={`${
                            useCompactControls ? "w-5 h-2.5" : "w-6 h-3"
                          } rounded-md border-2 border-current`}
                        />
                      </button>
                    </PortalTooltip>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="absolute -top-4 right-2 z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div
              className={`flex rounded-lg p-0.5 hover:shadow-xl transition-all duration-200 border ${
                layout.settings?.locked
                  ? "border-neutral-light/30 bg-accent-coral"
                  : "border-neutral-light/30 bg-dark-surface"
              }`}
            >
              <PortalTooltip
                content={
                  layout.settings?.locked
                    ? "Unlock widget position"
                    : "Lock widget position"
                }
                side="top"
              >
                <button
                  onClick={handleLockToggle}
                  className={`p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center ${
                    layout.settings?.locked
                      ? "bg-accent-coral hover:bg-accent-coral/80"
                      : "hover:bg-dark-elevated"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={layout.settings?.locked ? faLock : faLockOpen}
                    size="xs"
                    className="flex-shrink-0"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                </button>
              </PortalTooltip>
            </div>
          </div>

          {/* Vertical Alignment Controls for Rich Text Widget */}
          {isEditing &&
            !layout.settings?.locked &&
            widget.widget_type.type === "rich_text" &&
            !isMobileDevice &&
            layout[previewMode].columnHeight > 1 && (
              <div className="absolute top-1/2 -right-4 -translate-y-1/2 z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="flex flex-col bg-dark-surface rounded-lg p-1 shadow-lg border border-neutral-light/30">
                  <PortalTooltip content="Align top" side="left">
                    <button
                      onClick={() => handleVerticalAlignment("flex-start")}
                      className={`p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center ${
                        layout.settings?.verticalAlignment === "flex-start" ||
                        !layout.settings?.verticalAlignment
                          ? "bg-primary"
                          : "hover:bg-dark-elevated"
                      }`}
                    >
                      <AlignVerticalJustifyStart
                        size={14}
                        className="flex-shrink-0"
                      />
                    </button>
                  </PortalTooltip>
                  <PortalTooltip content="Align center" side="left">
                    <button
                      onClick={() => handleVerticalAlignment("center")}
                      className={`p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center ${
                        layout.settings?.verticalAlignment === "center"
                          ? "bg-primary"
                          : "hover:bg-dark-elevated"
                      }`}
                    >
                      <AlignVerticalJustifyCenter
                        size={14}
                        className="flex-shrink-0"
                      />
                    </button>
                  </PortalTooltip>
                  <PortalTooltip content="Align bottom" side="left">
                    <button
                      onClick={() => handleVerticalAlignment("flex-end")}
                      className={`p-1.5 w-7 h-7 rounded-md text-white transition-colors outline-none flex items-center justify-center ${
                        layout.settings?.verticalAlignment === "flex-end"
                          ? "bg-primary"
                          : "hover:bg-dark-elevated"
                      }`}
                    >
                      <AlignVerticalJustifyEnd
                        size={14}
                        className="flex-shrink-0"
                      />
                    </button>
                  </PortalTooltip>
                </div>
              </div>
            )}

          {/* Width Toggle Arrow for Skinny Modes */}
          {widget.widget_type.skinny &&
            layout[previewMode].columnHeight === 1 &&
            !isMobileDevice &&
            previewMode !== "mobile" && (
              <div className="absolute top-1/2 -right-3 -translate-y-1/2 z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="flex rounded-lg p-0.5 hover:shadow-xl transition-all duration-200 border border-neutral-light/30 bg-dark-surface">
                  <PortalTooltip
                    content={
                      layout[previewMode].columns === 4
                        ? widget.widget_type.skinny_full
                          ? "Increase width to 8x1"
                          : "Increase width"
                        : layout[previewMode].columns === 8
                          ? widget.widget_type.skinny_full
                            ? "Increase width to 12x1"
                            : "Decrease width"
                          : "Decrease width"
                    }
                    side="top"
                  >
                    <button
                      onClick={() => {
                        const currentCols = layout[previewMode].columns;
                        let newCols;

                        if (widget.widget_type.skinny_full) {
                          // Support 4x1 -> 8x1 -> 12x1 -> 4x1 cycle
                          if (currentCols === 4) {
                            newCols = 8;
                          } else if (currentCols === 8) {
                            newCols = 12;
                          } else {
                            newCols = 4;
                          }
                        } else {
                          // Original 4x1 <-> 8x1 toggle
                          newCols = currentCols === 4 ? 8 : 4;
                        }

                        handleColumnResize(newCols, 1);
                      }}
                      onMouseEnter={() => {
                        const currentCols = layout[previewMode].columns;
                        let newCols;

                        if (widget.widget_type.skinny_full) {
                          if (currentCols === 4) {
                            newCols = 8;
                          } else if (currentCols === 8) {
                            newCols = 12;
                          } else {
                            newCols = 4;
                          }
                        } else {
                          newCols = currentCols === 4 ? 8 : 4;
                        }

                        onResizePreviewStart?.(id, newCols, 1);
                      }}
                      onMouseLeave={() => onResizePreviewStop?.()}
                      className="p-1.5 w-7 h-7 rounded-md text-white/70 transition-colors outline-none flex items-center justify-center hover:bg-dark-elevated"
                    >
                      <FontAwesomeIcon
                        icon={
                          layout[previewMode].columns === 12
                            ? faArrowLeft
                            : faArrowRight
                        }
                        size="xs"
                        className="flex-shrink-0"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </button>
                  </PortalTooltip>
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
});

// Separate component to render settings outside the widget container
const WidgetSettingsPortal = memo(function WidgetSettingsPortal({
  showSettings,
  isMobileDevice,
  widget,
  config,
  layout,
  settingsType,
  onLayoutChange,
  onConfigChange,
  onClose,
  previewMode,
  onLockToggle,
  spaceData,
  allWidgets,
}) {
  if (!showSettings || isMobileDevice) return null;

  return (
    <WidgetSettings
      widget={widget}
      config={config}
      layout={layout}
      settingsType={settingsType}
      onLayoutChange={onLayoutChange}
      onConfigChange={onConfigChange}
      onClose={onClose}
      previewMode={previewMode}
      totalColumns={12}
      onLockToggle={onLockToggle}
      spaceData={spaceData}
      allWidgets={allWidgets}
    />
  );
});

EditableWidget.displayName = "EditableWidget";

export default EditableWidget;
export { WidgetSettingsPortal };
